import React, { useMemo, useCallback, useRef, useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { reactHookForm } from '@yola/ws-ui';
import {
  AI_SITE_WIZARD_SITE_PROP,
  aiSiteWizardSitePropValues,
} from 'src/js/constants/ai-site-wizard';
import useFeatureFlags from 'src/js/modules/feature-flags/hooks/use-feature-flags';
import siteSetup from '../modules/site-setup';
import GalleryLayout from '../components/gallery-layout';
import siteSetupComp from '../components/site-setup';
import getSiteIdToRecreate from '../modules/sites/selectors/get-site-id-recreate';
import getSiteIdToSwitch from '../modules/sites/selectors/site-id-to-switch-template';
import useTemplates from '../modules/templates/hooks/use-templates';
import getUserData from '../modules/user/selectors/get-user-data';
import getLocale from '../modules/i18n/selectors/locale';
import Layout from '../components/layout';
import getGalleryCaptions from '../utils/get-gallery-captions';
import trackers from '../modules/analytics/segment/trackers';
import routesMap from '../router/helpers/routes-map';
import {
  AI_SITE_WIZARD_FLAG,
  PREVIEW_COLUMNS_COUNT,
  BUSINESS_CATEGORY_FIELD_NAME,
  DESKTOP_VIEW_BREAKPOINT,
} from '../constants/common';
import { SiteSetupContext } from '../modules/site-setup/helpers/site-setup-context';
import useOnTemplateSelect from '../modules/site-setup/hooks/use-on-template-select';
import constants from '../modules/site-setup/constants';
import dialogs from '../modules/dialogs';
import user from '../modules/user';
import useMatchBreakpoint from '../utils/hooks/use-match-breakpoint';
import fetchTemplatesData from '../modules/templates/thunks/fetch-templates-data';
import getAllTemplatesCount from '../modules/templates/selectors/get-all-templates-count';
import splitTemplatesByRecommendation from '../modules/templates/helpers/split-templates-by-recommendation';
import { BLANK_TEMPLATE_BUILD_SLUG } from '../modules/templates/constants/common';

const { useForm } = reactHookForm;

const { helpers, constants: siteSetupConstants } = siteSetup;
const { GalleryLayoutStep } = siteSetupComp;
const { siteSetupNames } = siteSetupConstants;

const {
  trackPreviewClicked,
  trackEditingClicked,
  trackSitesetupStartWithBlankTemplateSelected,
  trackSitesetupBusinessCategoryProvided,
  trackSiteSetupLoadMoreTemplatesClicked,
  trackSiteSetupBackToTopClicked,
} = trackers;
const { triggerIds, common } = constants;

const FIRST_PAGE = 1;

function GalleryLayoutContainer() {
  const galleryGridRef = useRef(null);
  const categoriesBarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname: pathName } = location;
  const isTemplateRoute = helpers.isTemplateRoute(pathName);
  const context = useContext(SiteSetupContext);
  const isUserAuthorized = user.verifiers.isUserAuthorized();
  const { getArchetype, getData, updateData } = context;
  const contextData = getData();
  const archetype = getArchetype() || null;
  const isArchetypeSet = Boolean(archetype !== undefined);
  const [gridColumns, setGridColumns] = useState(PREVIEW_COLUMNS_COUNT.threeColumns);
  const dispatch = useDispatch();
  const [featureFlags] = useFeatureFlags([AI_SITE_WIZARD_FLAG]);
  const { [AI_SITE_WIZARD_FLAG]: isAISiteWizardEnabled } = featureFlags;

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      [BUSINESS_CATEGORY_FIELD_NAME]: contextData.businessCategory || '',
    },
    mode: 'onTouched',
  });
  const businessCategoryValue = watch(BUSINESS_CATEGORY_FIELD_NAME);

  const { templates } = useTemplates();

  const { recommendedTemplates, restTemplates } = useMemo(
    () => splitTemplatesByRecommendation(templates),
    [templates]
  );

  const [isNextTemplatesBatchLoading, setIsNextTemplatesBatchLoading] = useState(false);
  const [isFirstTemplatesBatchLoading, setIsFirstTemplatesBatchLoading] = useState(false);
  const [isFloatingButtonVisible, setIsFloatingButtonVisible] = useState(false);
  const allTemplatesCount = useSelector(getAllTemplatesCount);
  const canLoadMore = allTemplatesCount > templates.length;

  const userData = useSelector(getUserData);
  const locale = useSelector(getLocale);
  const siteIdToRecreate = useSelector(getSiteIdToRecreate);
  const siteIdToSwitch = useSelector(getSiteIdToSwitch);
  const handleTemplateSelect = useOnTemplateSelect();
  const siteId = siteIdToRecreate || siteIdToSwitch || null;
  const isDesktopView = useMatchBreakpoint(DESKTOP_VIEW_BREAKPOINT);
  // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  const captions = useMemo(
    () =>
      getGalleryCaptions({
        siteIdToSwitch,
        siteIdToRecreate,
        currentCount: templates.length,
        allCount: allTemplatesCount,
      }),
    [allTemplatesCount, templates, siteIdToRecreate, siteIdToSwitch]
  );
  const traits = useMemo(
    () => ({
      siteId,
      businessCategory: contextData.businessCategorySlug,
    }),
    [siteId, contextData.businessCategorySlug]
  );

  const isGalleryLayoutStep = !siteIdToSwitch && !isTemplateRoute;

  const scrollToGalleryGridTop = useCallback(() => {
    const { current: categoriesBar } = categoriesBarRef;
    const { current: galleryGrid } = galleryGridRef;

    if (!galleryGrid || !categoriesBar) {
      return;
    }

    const { bottom: categoriesBarBottom } = categoriesBar.getBoundingClientRect();
    const { top: galleryGridTop } = galleryGrid.getBoundingClientRect();

    if (galleryGridTop >= categoriesBarBottom) {
      return;
    }

    window.scrollTo({
      top: galleryGrid.offsetTop - categoriesBarBottom,
      behavior: 'smooth',
    });
  }, [categoriesBarRef, galleryGridRef]);

  const fetchTemplatesDataWithBusinessCategory = useCallback(
    async (businessCategory = null) => {
      setIsFirstTemplatesBatchLoading(true);

      try {
        await dispatch(
          fetchTemplatesData({
            page: FIRST_PAGE,
            pageSize: common.TEMPLATES_PAGE_SIZE,
            businessCategory,
            locale,
          })
        );
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setIsFirstTemplatesBatchLoading(false);
      }
    },
    [dispatch, locale]
  );

  const handleCategorySelect = useCallback(
    (selectedOption) => {
      handleSubmit(async () => {
        const selectedArchetype = selectedOption?.archetype || contextData.archetype;

        if (selectedOption) {
          updateData({
            businessCategory: selectedOption.label,
            businessCategorySlug: selectedOption.value,
            archetype: selectedArchetype,
            templates: selectedOption.templates || [],
          });

          await fetchTemplatesDataWithBusinessCategory(selectedOption.value);

          if (!isDesktopView) {
            trackSitesetupBusinessCategoryProvided({
              siteId,
              stepId: siteSetupNames.GALLERY,
              businessCategory: selectedOption?.value || contextData.businessCategorySlug,
              archetypeId: selectedArchetype,
            });
          }
        }
      })();

      scrollToGalleryGridTop();
    },
    [
      handleSubmit,
      scrollToGalleryGridTop,
      contextData.archetype,
      contextData.businessCategorySlug,
      updateData,
      fetchTemplatesDataWithBusinessCategory,
      isDesktopView,
      siteId,
    ]
  );

  const handleTemplatePreview = useCallback(
    (templateSlug) => {
      trackPreviewClicked({
        ...traits,
        templateBuildSlug: templateSlug,
        archetypeId: archetype || null,
      });

      const templateDetailsLink = routesMap.templateDetails.url({
        templateSlug,
      });

      navigate(templateDetailsLink);
    },
    [navigate, archetype, traits]
  );

  const handleTemplateEditing = useCallback(
    (templateSlug) => {
      trackEditingClicked({
        ...traits,
        templateBuildSlug: templateSlug,
        archetypeId: archetype || null,
      });

      handleTemplateSelect(templateSlug, triggerIds.TEMPLATE);
    },
    [traits, archetype, handleTemplateSelect]
  );

  const handleBlankTemplateSelect = () => {
    const { stepName } = helpers.getCurrentStep();

    trackSitesetupStartWithBlankTemplateSelected({
      stepId: stepName,
      businessCategory: contextData.businessCategorySlug,
      archetypeId: archetype || null,
    });

    updateData({ [AI_SITE_WIZARD_SITE_PROP]: aiSiteWizardSitePropValues.SKIPPED });
    handleTemplateSelect(BLANK_TEMPLATE_BUILD_SLUG, triggerIds.BLANK_TEMPLATE);
  };

  const resetData = async () => {
    await fetchTemplatesDataWithBusinessCategory();

    updateData({
      archetype: null,
      businessCategory: null,
      businessCategorySlug: null,
      templates: [],
    });
  };

  const onFilterTemplateClick = () => {
    dispatch(
      dialogs.actions.show(dialogs.dialogTypes.FILTER_TEMPLATES_DIALOG, {
        control,
        contextData,
        resetData,
        resetForm: reset,
        onSubmit: handleCategorySelect,
      })
    );
  };

  useEffect(
    () => {
      if (!businessCategoryValue && isDesktopView) {
        resetData();
      }
    },
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [businessCategoryValue]
  );

  const handleLoadMoreTemplatesClick = async () => {
    if (isNextTemplatesBatchLoading) return;
    setIsNextTemplatesBatchLoading(true);

    try {
      const currentBatch = Math.ceil(templates.length / common.TEMPLATES_PAGE_SIZE);
      const nextPage = currentBatch + 1;

      await dispatch(
        fetchTemplatesData({
          pageSize: common.TEMPLATES_PAGE_SIZE,
          page: nextPage,
          businessCategory: contextData.businessCategorySlug || null,
          locale,
        })
      );

      const { stepName } = helpers.getCurrentStep();

      trackSiteSetupLoadMoreTemplatesClicked({
        siteId,
        stepId: stepName,
        businessCategory: contextData.businessCategorySlug,
        archetypeId: archetype || null,
      });
    } catch (error) {
      console.error('Failed to load more templates:', error);
    } finally {
      setIsNextTemplatesBatchLoading(false);
    }
  };

  useEffect(() => {
    const categoryNode = categoriesBarRef.current;
    const galleryNode = galleryGridRef.current;
    if (!categoryNode || !galleryNode) return;

    const handleScroll = () => {
      const { bottom: categoryBottom } = categoryNode.getBoundingClientRect();
      const { top: galleryTop } = galleryNode.getBoundingClientRect();

      const shouldShow = galleryTop < categoryBottom;

      setIsFloatingButtonVisible((prev) => (prev !== shouldShow ? shouldShow : prev));
    };

    window.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [recommendedTemplates]);

  if (isUserAuthorized && !isArchetypeSet && !siteIdToSwitch) {
    navigate(routesMap.siteCategory.url(), { replace: true });
    return null;
  }

  if (!templates.length || !userData || !locale) return <Layout />;

  const handleColumnsToggle = (columnsCount) => {
    setGridColumns(columnsCount);
  };

  const handleFloatingButtonClick = () => {
    const { stepName } = helpers.getCurrentStep();

    scrollToGalleryGridTop();

    trackSiteSetupBackToTopClicked({
      siteId,
      stepId: stepName,
      businessCategory: contextData.businessCategorySlug,
      archetypeId: archetype || null,
    });
  };

  return (
    <>
      {isGalleryLayoutStep ? (
        <GalleryLayoutStep
          isAISiteWizardEnabled={isAISiteWizardEnabled}
          captions={captions}
          control={control}
          siteId={siteId}
          locale={locale}
          templates={restTemplates}
          recommendedTemplates={recommendedTemplates}
          categoriesBarRef={categoriesBarRef}
          galleryGridRef={galleryGridRef}
          isSiteToSwitch={Boolean(siteIdToSwitch)}
          onCategorySelect={handleCategorySelect}
          onTemplatePreviewClick={handleTemplatePreview}
          onTemplateEditClick={handleTemplateEditing}
          onBlankTemplateClick={handleBlankTemplateSelect}
          gridColumns={gridColumns}
          canLoadMore={canLoadMore}
          isNextTemplatesBatchLoading={isNextTemplatesBatchLoading}
          isFirstTemplatesBatchLoading={isFirstTemplatesBatchLoading}
          isFloatingButtonVisible={isFloatingButtonVisible}
          onLoadMoreClick={handleLoadMoreTemplatesClick}
          onFloatingButtonClick={handleFloatingButtonClick}
          handleColumnsToggle={handleColumnsToggle}
          onFilterTemplateClick={onFilterTemplateClick}
        />
      ) : (
        <GalleryLayout
          isAISiteWizardEnabled={isAISiteWizardEnabled}
          captions={captions}
          control={control}
          siteId={siteId}
          locale={locale}
          templates={restTemplates}
          recommendedTemplates={recommendedTemplates}
          categoriesBarRef={categoriesBarRef}
          galleryGridRef={galleryGridRef}
          isSiteToSwitch={Boolean(siteIdToSwitch)}
          canLoadMore={canLoadMore}
          isNextTemplatesBatchLoading={isNextTemplatesBatchLoading}
          isFirstTemplatesBatchLoading={isFirstTemplatesBatchLoading}
          isFloatingButtonVisible={isFloatingButtonVisible}
          onLoadMoreClick={handleLoadMoreTemplatesClick}
          onFloatingButtonClick={handleFloatingButtonClick}
          onCategorySelect={handleCategorySelect}
          onTemplatePreviewClick={handleTemplatePreview}
          onTemplateEditClick={handleTemplateEditing}
          onBlankTemplateClick={handleBlankTemplateSelect}
          gridColumns={gridColumns}
          handleColumnsToggle={handleColumnsToggle}
        />
      )}
      <Outlet />
    </>
  );
}

export default GalleryLayoutContainer;
